import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { SettingsService } from '@app/core/settings/settings.service';
import { MemberService } from '@app/core/members/member.service';
import { PropertiesFormComponent } from '@app/+profile-form/components/properties-form/properties-form.component';
import { LoadMember } from '@app/core/store/member/member-actions';
import { Store } from '@ngrx/store';
import * as fromRoot from '@app/core/store/reducers';
import { TopNavbarComponent } from '@app/shared/components/top-navbar/top-navbar.component';
import { TranslatePipe } from '@ngx-translate/core';
import { LoyaltyClubHeaderComponent } from '@app/shared/components/loyalty-club-header/loyalty-club-header.component';
import { AsyncPipe } from '@angular/common';
import { ConsentFormComponent } from '@app/+profile-form/components/consent-form/consent-form.component';
import { UPDATE_CONFIRMATION_PATH } from '@app/+profile-form/profile-routes';
import { REDIRECT_QUERY_PARAM } from '@app/core/consts';
import { MissingPropertiesService } from '@app/core/missing-properties.service';

@Component({
  selector: 'app-forced-update',
  templateUrl: './forced-update.component.html',
  imports: [
    TopNavbarComponent,
    TranslatePipe,
    LoyaltyClubHeaderComponent,
    AsyncPipe,
    ConsentFormComponent,
    PropertiesFormComponent,
  ],
})
export class ForcedUpdateComponent {
  @ViewChild(PropertiesFormComponent) public propertiesComponent: PropertiesFormComponent;

  protected properties$ = this.settings.getSettingsOnce().pipe(
    map(settings => settings.schemaObject),
  );
  protected missingPropertyNames$: Observable<string[]> = this.missingPropertiesService.getMissingPropertyNames();
  protected forceConsents$ = new ReplaySubject<boolean>();
  protected forceProperties$ = new ReplaySubject<boolean>();
  protected showConsentsForm$ = combineLatest([
    this.forceConsents$,
    this.forceProperties$,
  ]).pipe(
    map(([forceConsents, forceProperties]) => forceConsents && !forceProperties),
  );
  protected showPropertiesForm$ = combineLatest([
    this.forceProperties$,
    this.missingPropertyNames$,
  ]).pipe(
    map(([forceProperties, missingPropertyNames]) => forceProperties && missingPropertyNames?.length > 0),
  );

  constructor(private route: ActivatedRoute,
              private router: Router,
              private member: MemberService,
              private settings: SettingsService,
              private missingPropertiesService: MissingPropertiesService,
              private store: Store<fromRoot.State>) {
    this.route.queryParamMap
      .pipe(map(params => params.get('properties') === 'true'))
      .subscribe(this.forceProperties$);

    this.route.queryParamMap
      .pipe(map(params => params.get('consents') === 'true'))
      .subscribe(this.forceConsents$);
  }

  public onSubmitProperties(): void {
    if (this.propertiesComponent.form.errors) {
      return;
    }

    this.updateMember().pipe(
      withLatestFrom(this.forceConsents$),
    ).subscribe(([, forceConsents]) => {
      this.store.dispatch(new LoadMember());
      forceConsents ? this.forceProperties$.next(false) : this.navigateAfterUpdate();
    });
  }

  private updateMember(): Observable<unknown> {
    return this.member.get()
      .pipe(
        take(1),
        withLatestFrom(this.getMissingPropertiesValues()),
        switchMap(([member, properties]) =>
          this.member.update({ ...member?.properties, ...properties }),
        ),
      );
  }

  private getMissingPropertiesValues(): Observable<Record<string, any>> {
    return this.missingPropertyNames$.pipe(
      map(propertyNames => Object.entries(this.propertiesComponent.form.value)
        .filter(([name]) => propertyNames.includes(name))
        .reduce((acc, [name, value]) => ({ ...acc, [name]: value }), {})),
    );
  }

  private navigateAfterUpdate(): void {
    this.route.queryParamMap.subscribe(paramMap => {
      const redirect = paramMap.get(REDIRECT_QUERY_PARAM);
      if (redirect) {
        this.router.navigateByUrl(redirect, { replaceUrl: true });
      } else {
        this.router.navigate(
          ['../', UPDATE_CONFIRMATION_PATH],
          { relativeTo: this.route, replaceUrl: true },
        );
      }
    });

  }
}
